// Portfolio Administration v1.0 PR 4a §7.5 — manual lien-search entry modal.
// CSC/CT API integration is deferred (spec §3); the Order flow today is a
// manual-data-entry surface where the user records a search they ordered
// out-of-band.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useRecordUccLienSearch } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface OrderLienSearchModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

export function OrderLienSearchModal({
  loanId,
  open,
  onClose,
}: OrderLienSearchModalProps) {
  const toast = useToast();
  const record = useRecordUccLienSearch(loanId);

  const tz = useUserTimeZone();
  const today = isoDateInTz(new Date(), tz);
  const [purpose, setPurpose] = useState("PostFilingReflection");
  const [debtorName, setDebtorName] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [orderedDate, setOrderedDate] = useState(today);
  const [resultsCount, setResultsCount] = useState("0");
  const [expected, setExpected] = useState("");
  const [observed, setObserved] = useState("");

  const onSubmit = async () => {
    if (!debtorName.trim() || !jurisdiction.trim()) {
      toast.error("Debtor name and jurisdiction are required.");
      return;
    }
    try {
      await record.mutateAsync({
        loanId,
        searchPurpose: purpose,
        searchedDebtorName: debtorName,
        searchedJurisdiction: jurisdiction,
        searchOrderedDate: orderedDate,
        searchResultsCount: Number(resultsCount) || 0,
        expectedPriorityPosition: expected ? Number(expected) : null,
        observedPriorityPosition: observed ? Number(observed) : null,
      });
      toast.success("Lien search recorded.");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not record search.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Record UCC lien search"
      description="CSC / CT Lien Solutions API integration is deferred. Enter the search details manually."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={record.isPending}
            data-testid="ucc-search-submit"
          >
            Record search
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-space-2" data-testid="ucc-search-form">
        <label className="flex flex-col gap-space-1 text-caption col-span-2">
          <span className="text-gray-mid uppercase tracking-label">Search purpose</span>
          <select
            className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          >
            <option value="PreFilingDueDiligence">Pre-filing due diligence</option>
            <option value="PostFilingReflection">Post-filing reflection</option>
            <option value="AnnualMonitoring">Annual monitoring</option>
            <option value="PreContinuation">Pre-continuation refresh</option>
            <option value="PostPayoff">Post-payoff confirmation</option>
            <option value="Other">Other</option>
          </select>
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Debtor name</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={debtorName}
            onChange={(e) => setDebtorName(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Jurisdiction</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            placeholder="DE / CA / …"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Ordered date</span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={orderedDate}
            onChange={(e) => setOrderedDate(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Results count</span>
          <input
            type="number"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={resultsCount}
            onChange={(e) => setResultsCount(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Expected priority</span>
          <input
            type="number"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            placeholder="1, 2, …"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Observed priority</span>
          <input
            type="number"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={observed}
            onChange={(e) => setObserved(e.target.value)}
            placeholder="1, 2, …"
          />
        </label>
      </div>
    </Modal>
  );
}
